import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/v2/lib/utils";
import { useIsMobile } from "@/v2/hooks/useIsMobile";
import {
  smartSearch,
  splitByMatches,
  highlightTokensInText,
  type SearchableField,
  type MatchRange,
  type SearchResult,
} from "@/shared/utils/smartSearch";

// ── Types ──────────────────────────────────────────────────────────

export interface ComboBoxProps<T> {
  items: T[];
  value: string | null;
  onChange: (value: string) => void;
  /** Search field extractors passed to smartSearch */
  fields: ((item: T) => SearchableField)[];
  /** Unique key for each item */
  getKey: (item: T) => string;
  /** Display label when item is selected (shown in the input) */
  getDisplayLabel: (item: T) => string;
  /** Render a single option row. Receives match ranges and tokens for highlighting. */
  renderItem?: (
    item: T,
    highlighted: boolean,
    matches: Map<number, MatchRange[]>,
    tokens?: string[],
  ) => React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  disabledMessage?: string;
  error?: string;
  className?: string;
  onBlur?: () => void;
  /** Max results to show (default 50) */
  limit?: number;
  /** Max items to show when there's no search query (defaults to `limit`). */
  defaultLimit?: number;
  /** Show clear (X) button when a value is selected */
  clearable?: boolean;
  onClear?: () => void;
  /** Text to pre-fill when the input is focused via Tab */
  getSearchValue?: (item: T) => string;
  /** Render a rich display inside the input when an item is selected */
  renderSelected?: (item: T) => React.ReactNode;
  /** Width of the dropdown (default: match input width) */
  dropdownWidth?: string;
}

// ── Component ──────────────────────────────────────────────────────

export function ComboBox<T>({
  items,
  value,
  onChange,
  fields,
  getKey,
  getDisplayLabel,
  renderItem,
  placeholder = "Search...",
  disabled = false,
  disabledMessage,
  error,
  className,
  onBlur,
  limit = 50,
  defaultLimit,
  clearable = false,
  onClear,
  getSearchValue,
  renderSelected,
  dropdownWidth,
}: ComboBoxProps<T>) {
  const isMobile = useIsMobile();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const justFocusedRef = React.useRef(false);
  const pointerFocusRef = React.useRef(false);

  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(-1);

  // Mobile full-screen sheet state
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [mobileInputValue, setMobileInputValue] = React.useState("");

  // Find selected item
  const selectedItem = React.useMemo(() => {
    if (!value) return null;
    return items.find((item) => getKey(item) === value) ?? null;
  }, [items, value, getKey]);

  const displayText = selectedItem ? getDisplayLabel(selectedItem) : "";

  // Search results (used by both modes)
  const currentQuery = isMobile && mobileOpen ? mobileInputValue : inputValue;

  const results: SearchResult<T>[] = React.useMemo(() => {
    if (!currentQuery.trim()) {
      const cap = defaultLimit ?? limit;
      return items.slice(0, cap).map((item) => ({
        item,
        score: 0,
        matches: new Map<number, MatchRange[]>(),
        matchedTokens: [] as string[],
      }));
    }
    return smartSearch(items, currentQuery, fields, { limit });
  }, [items, currentQuery, fields, limit, defaultLimit]);

  // Scroll active item into view
  React.useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const activeEl = listRef.current.children[activeIndex] as HTMLElement;
    activeEl?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // ── Handlers ───────────────────────────────────────────────────

  const openDropdown = React.useCallback(() => {
    if (disabled) return;
    if (isMobile) {
      setMobileOpen(true);
      setMobileInputValue("");
      setActiveIndex(-1);
    } else {
      setOpen(true);
      setActiveIndex(-1);
    }
  }, [disabled, isMobile]);

  const closeDropdown = React.useCallback(() => {
    if (isMobile) {
      setMobileOpen(false);
      setMobileInputValue("");
    } else {
      setOpen(false);
    }
    setActiveIndex(-1);
  }, [isMobile]);

  const selectItem = React.useCallback(
    (item: T) => {
      const key = getKey(item);
      onChange(key);
      setInputValue("");
      setMobileInputValue("");
      closeDropdown();
    },
    [getKey, onChange, closeDropdown],
  );

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setActiveIndex(-1);
      if (!open) {
        setOpen(true);
        setActiveIndex(-1);
      }
    },
    [open],
  );

  const handleInputFocus = React.useCallback(() => {
    if (isMobile) return;
    justFocusedRef.current = true;
    if (pointerFocusRef.current) {
      pointerFocusRef.current = false;
    } else {
      // Tab focus: pre-fill + select-all
      if (selectedItem) {
        const prefill = getSearchValue ? getSearchValue(selectedItem) : getDisplayLabel(selectedItem);
        setInputValue(prefill);
        requestAnimationFrame(() => inputRef.current?.select());
      }
    }
    setOpen(true);
    setActiveIndex(-1);
  }, [isMobile, selectedItem, getSearchValue, getDisplayLabel]);

  const handleInputClick = React.useCallback(() => {
    if (isMobile) {
      openDropdown();
      return;
    }
    if (justFocusedRef.current) {
      justFocusedRef.current = false;
      return;
    }
    if (open) {
      setInputValue("");
      closeDropdown();
    } else {
      openDropdown();
    }
  }, [isMobile, open, openDropdown, closeDropdown]);

  const handleInputBlur = React.useCallback(
    (e: React.FocusEvent) => {
      if (isMobile) return;
      if (containerRef.current?.contains(e.relatedTarget as Node)) return;
      setInputValue("");
      setOpen(false);
      setActiveIndex(-1);
      onBlur?.();
    },
    [isMobile, onBlur],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled || isMobile) return;

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          if (!open) {
            setOpen(true);
            setActiveIndex(-1);
          } else {
            setActiveIndex((prev) =>
              prev < results.length - 1 ? prev + 1 : prev,
            );
          }
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          if (!open) {
            setOpen(true);
            setActiveIndex(-1);
          } else {
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
          }
          break;
        }
        case "Enter": {
          e.preventDefault();
          if (open && results.length > 0) {
            const idx = activeIndex >= 0 ? activeIndex : 0;
            selectItem(results[idx].item);
            const container = inputRef.current?.closest("form") ?? inputRef.current?.closest("[role='dialog']");
            if (container) {
              const focusable = Array.from(
                container.querySelectorAll<HTMLElement>(
                  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([tabindex="-1"])',
                ),
              );
              const curIdx = focusable.indexOf(inputRef.current!);
              focusable[curIdx + 1]?.focus();
            }
          }
          break;
        }
        case "Escape": {
          if (open) {
            e.preventDefault();
            e.stopPropagation();
            setInputValue("");
            setOpen(false);
            setActiveIndex(-1);
          }
          break;
        }
        case "Tab": {
          if (open && results.length > 0) {
            const idx = activeIndex >= 0 ? activeIndex : 0;
            selectItem(results[idx].item);
          } else {
            setInputValue("");
            setOpen(false);
            setActiveIndex(-1);
          }
          break;
        }
      }
    },
    [disabled, isMobile, open, activeIndex, results, selectItem],
  );

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onClear?.();
      setInputValue("");
      closeDropdown();
      inputRef.current?.blur();
    },
    [onClear, closeDropdown],
  );

  const handleOptionMouseDown = React.useCallback(
    (e: React.MouseEvent, item: T) => {
      e.preventDefault();
      selectItem(item);
      inputRef.current?.blur();
    },
    [selectItem],
  );

  const handleMobileItemClick = React.useCallback(
    (item: T) => {
      selectItem(item);
    },
    [selectItem],
  );

  // ── Default item renderer ──────────────────────────────────────

  const defaultRenderItem = React.useCallback(
    (item: T, _highlighted: boolean, _matches: Map<number, MatchRange[]>, tokens?: string[]) => {
      const label = getDisplayLabel(item);
      if (!tokens?.length) return label;

      const ranges = highlightTokensInText(label, tokens);
      if (!ranges.length) return label;

      const parts = splitByMatches(label, ranges);
      return (
        <>
          {parts.map((part, i) =>
            part.highlighted ? (
              <mark key={i} className="bg-primary/20 text-inherit rounded-sm">
                {part.text}
              </mark>
            ) : (
              <span key={i}>{part.text}</span>
            ),
          )}
        </>
      );
    },
    [getDisplayLabel],
  );

  const renderer = renderItem ?? defaultRenderItem;

  // ── Render helpers ──────────────────────────────────────────────

  const isSearching = inputValue.trim().length > 0;

  const renderOptions = (mobile: boolean) => (
    <ul
      ref={listRef}
      role="listbox"
      className={cn(
        mobile
          ? "flex-1 overflow-y-auto overscroll-contain"
          : cn(
              "absolute z-50 mt-1 w-full max-h-[300px] overflow-y-auto overflow-x-hidden",
              "rounded-md border bg-card shadow-md border-border",
            ),
      )}
      style={!mobile && dropdownWidth ? { width: dropdownWidth } : undefined}
    >
      {results.length === 0 ? (
        <li className="px-3 py-6 text-center text-sm text-muted-foreground">
          No results found.
        </li>
      ) : (
        results.map((result, index) => {
          const key = getKey(result.item);
          const isActive = index === activeIndex;
          const isSelected = key === value;

          return (
            <li
              key={key}
              id={`combobox-option-${index}`}
              role="option"
              aria-selected={isSelected}
              onMouseDown={mobile ? undefined : (e) => handleOptionMouseDown(e, result.item)}
              onClick={mobile ? () => handleMobileItemClick(result.item) : undefined}
              onMouseEnter={mobile ? undefined : () => setActiveIndex(index)}
              className={cn(
                "relative flex cursor-pointer select-none items-center px-3 text-sm overflow-hidden",
                "text-foreground",
                mobile ? "py-3 min-h-[44px] active:bg-accent" : "py-1.5",
                isActive && !mobile && "bg-accent text-accent-foreground",
                !isActive && !mobile && "hover:bg-accent/50",
                isSelected && mobile && "bg-accent/30",
              )}
            >
              <div className="min-w-0 flex-1 overflow-hidden">
                {renderer(result.item, isActive, result.matches, result.matchedTokens)}
              </div>
            </li>
          );
        })
      )}
    </ul>
  );

  return (
    <div ref={containerRef} className={cn("relative min-w-0", className)}>
      {/* Input trigger */}
      <div
        className={cn(
          "relative flex min-w-0 items-center gap-1 rounded-md border px-3 py-2 text-sm",
          renderSelected && "min-h-[58px]",
          "bg-card border-border text-foreground",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1",
          disabled && "opacity-50 cursor-not-allowed",
          error && "border-red-500 bg-red-50 dark:border-red-700 dark:bg-red-900/40",
        )}
      >
        {/* Rich selected display */}
        {renderSelected && selectedItem && (
          <div
            className={cn(
              "flex-1 min-w-0 overflow-hidden",
              (open || isSearching) ? "invisible" : "cursor-default",
            )}
            onPointerDown={() => { pointerFocusRef.current = true; }}
            onClick={() => {
              if (isMobile) {
                openDropdown();
              } else {
                inputRef.current?.focus();
              }
            }}
          >
            {renderSelected(selectedItem)}
          </div>
        )}

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open || mobileOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `combobox-option-${activeIndex}` : undefined
          }
          aria-invalid={!!error}
          disabled={disabled}
          readOnly={isMobile}
          placeholder={
            disabled && disabledMessage
              ? disabledMessage
              : open
                ? placeholder
                : selectedItem
                  ? displayText
                  : placeholder
          }
          value={isMobile ? "" : (isSearching || open ? inputValue : "")}
          onChange={isMobile ? undefined : handleInputChange}
          onPointerDown={() => { pointerFocusRef.current = true; }}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            "bg-transparent outline-none placeholder:text-muted-foreground min-w-0 text-ellipsis overflow-hidden",
            renderSelected && selectedItem
              ? cn("absolute inset-0 px-3 py-2", clearable && "pr-9", !open && !isSearching && "opacity-0 pointer-events-none")
              : cn("flex-1", !isSearching && !open && selectedItem && "placeholder:text-foreground"),
            isMobile && "cursor-pointer",
          )}
          autoComplete="off"
        />

        {/* Clear button */}
        {clearable && selectedItem && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={handleClear}
            className="shrink-0 text-muted-foreground hover:text-foreground min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Desktop dropdown */}
      {!isMobile && open && !disabled && renderOptions(false)}

      {/* Mobile full-screen sheet — nested Radix Dialog so it layers properly with parent dialog */}
      {isMobile && (
        <DialogPrimitive.Root open={mobileOpen} onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setMobileOpen(false);
            setMobileInputValue("");
            setActiveIndex(-1);
            onBlur?.();
          }
        }}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Content
              className={cn(
                "fixed inset-x-0 top-0 z-50 flex flex-col bg-background overflow-hidden",
                "data-[state=open]:animate-in data-[state=closed]:animate-out",
                "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
                "duration-200",
              )}
              style={{ height: "100dvh" }}
              onOpenAutoFocus={(e) => e.preventDefault()}
              onPointerDownOutside={(e) => e.preventDefault()}
              onInteractOutside={(e) => e.preventDefault()}
            >
              <DialogPrimitive.Title className="sr-only">
                {placeholder}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                Search and select an option
              </DialogPrimitive.Description>

              {/* Mobile header */}
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <input
                  type="text"
                  placeholder={placeholder}
                  value={mobileInputValue}
                  onChange={(e) => {
                    setMobileInputValue(e.target.value);
                    setActiveIndex(-1);
                  }}
                  className="flex-1 h-10 bg-secondary rounded-md px-3 text-sm text-foreground placeholder:text-muted-foreground outline-none"
                  autoComplete="off"
                />
                <DialogPrimitive.Close className="shrink-0 h-10 w-10 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground">
                  <X className="h-5 w-5" />
                </DialogPrimitive.Close>
              </div>

              {/* Mobile results */}
              {renderOptions(true)}
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      )}

      {/* Error message */}
      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}

export default ComboBox;
